import { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ImageField from "@/components/admin/ImageField";
import SectionMetadataFields from "@/components/admin/SectionMetadataFields";
import {
  adminCreatePageSection,
  adminCreateSponsor,
  adminCreateTeamMember,
  adminDeletePageSection,
  adminDeleteSponsor,
  adminDeleteTeamMember,
  adminGetContent,
  adminUpdatePageSection,
  adminUpdateSponsor,
  adminUpdateTeamMember,
  formatError,
} from "@/lib/api";

const inputCls = "field-underline w-full font-dm text-[#FAFAFA] py-2.5 text-sm placeholder:text-[#A3A3A3]/50";
const labelCls = "font-oswald uppercase tracking-[0.2em] text-[11px] text-[#A3A3A3] block mb-1";
const goldBtn = "font-oswald uppercase tracking-[0.15em] text-xs bg-[#E85A24] text-black px-5 py-2.5 hover:bg-[#FF7038] transition-colors duration-300 disabled:opacity-60 inline-flex items-center gap-2";

const emptySection = {
  key: "",
  eyebrow: "",
  title: "",
  title_accent: "",
  body: "",
  button_label: "",
  button_url: "",
  image: "",
  metadata: {},
  sort_order: 0,
  is_active: true,
};

const emptySponsor = {
  name: "",
  logo: "",
  website_url: "",
  tier: "",
  sort_order: 0,
  is_active: true,
};

const emptyTeamMember = {
  name: "",
  role: "",
  bio: "",
  image: "",
  instagram_url: "",
  linkedin_url: "",
  sort_order: 0,
  is_active: true,
};

function Field({ label, value, onChange, type = "text", required = false, span = false, placeholder = "" }) {
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <label className={labelCls}>{label}</label>
      <input
        type={type}
        min={type === "number" ? 0 : undefined}
        required={required}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(event) => onChange(type === "number" ? Number(event.target.value) : event.target.value)}
        className={inputCls}
      />
    </div>
  );
}

function TextArea({ label, value, onChange, rows = 4 }) {
  return (
    <div className="sm:col-span-2">
      <label className={labelCls}>{label}</label>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`${inputCls} resize-y`}
      />
    </div>
  );
}

function ActiveField({ value, onChange }) {
  return (
    <label className="sm:col-span-2 flex items-center justify-between gap-4 border border-white/10 p-4 cursor-pointer">
      <span>
        <span className="font-oswald uppercase tracking-[0.2em] text-xs text-[#FAFAFA] block">Visible on website</span>
        <span className="font-dm text-xs text-[#A3A3A3]">Turn this off to hide the item without deleting it.</span>
      </span>
      <input
        type="checkbox"
        checked={Boolean(value)}
        onChange={(event) => onChange(event.target.checked)}
        className="h-5 w-5 accent-[#E85A24]"
      />
    </label>
  );
}

function DeleteButton({ label, onConfirm }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="text-[#A3A3A3] hover:text-red-400 transition-colors p-2" aria-label={`Delete ${label}`}>
          <Trash2 size={16} />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-[#0a0a0a] border border-white/10 text-[#FAFAFA] rounded-none">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-anton uppercase">Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription className="font-dm text-[#A3A3A3]">This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-white/20 bg-transparent text-[#A3A3A3] hover:bg-white/5 hover:text-white">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="rounded-none bg-red-500/90 text-white hover:bg-red-500">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function RecordDialog({ kind, initial, title, onSave, trigger, authHeader }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ ...initial, metadata: initial.metadata || {} });
  }, [initial, open]);

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      await onSave(payload);
      setOpen(false);
    } catch (error) {
      toast.error(formatError(error, "Save failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-[#FAFAFA] rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-anton uppercase text-2xl text-[#FAFAFA]">{title}</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
          {kind === "section" && (
            <>
              <Field label="Section key" value={form.key} onChange={set("key")} required span placeholder="hero, footer, sponsors…" />
              <Field label="Eyebrow / section label" value={form.eyebrow} onChange={set("eyebrow")} span />
              <Field label="Title" value={form.title} onChange={set("title")} />
              <Field label="Accent title" value={form.title_accent} onChange={set("title_accent")} />
              <TextArea label="Body text" value={form.body} onChange={set("body")} rows={5} />
              <Field label="Button label" value={form.button_label} onChange={set("button_label")} />
              <Field label="Button URL / section anchor" value={form.button_url} onChange={set("button_url")} placeholder="#events or https://…" />
              <ImageField label="Section image" value={form.image} onChange={set("image")} folder="general" authHeader={authHeader} />
              <SectionMetadataFields sectionKey={form.key} metadata={form.metadata || {}} onChange={set("metadata")} />
            </>
          )}

          {kind === "sponsor" && (
            <>
              <Field label="Sponsor name" value={form.name} onChange={set("name")} required span />
              <Field label="Website URL" value={form.website_url} onChange={set("website_url")} type="url" />
              <Field label="Tier" value={form.tier} onChange={set("tier")} placeholder="Title, Gold, Partner…" />
              <ImageField label="Sponsor logo" value={form.logo} onChange={set("logo")} folder="sponsors" authHeader={authHeader} />
            </>
          )}

          {kind === "team" && (
            <>
              <Field label="Name" value={form.name} onChange={set("name")} required />
              <Field label="Role" value={form.role} onChange={set("role")} required />
              <TextArea label="Bio" value={form.bio} onChange={set("bio")} rows={5} />
              <Field label="Instagram URL" value={form.instagram_url} onChange={set("instagram_url")} type="url" />
              <Field label="LinkedIn URL" value={form.linkedin_url} onChange={set("linkedin_url")} type="url" />
              <ImageField label="Team member photo" value={form.image} onChange={set("image")} folder="team" authHeader={authHeader} />
            </>
          )}

          <Field label="Display order" value={form.sort_order} onChange={set("sort_order")} type="number" required />
          <div />
          <ActiveField value={form.is_active} onChange={set("is_active")} />

          <DialogFooter className="sm:col-span-2 mt-4">
            <button type="submit" disabled={saving} className={goldBtn}>{saving ? "Saving…" : "Save"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Status({ active }) {
  return (
    <span className={`inline-flex items-center gap-1 font-oswald uppercase tracking-[0.15em] text-[10px] ${active ? "text-emerald-400" : "text-[#A3A3A3]"}`}>
      {active ? <Eye size={12} /> : <EyeOff size={12} />}
      {active ? "Visible" : "Hidden"}
    </span>
  );
}

function EmptyState({ children }) {
  return <p className="p-6 font-dm text-sm text-[#A3A3A3]">{children}</p>;
}

export default function ContentManagement({ authHeader = {} }) {
  const [content, setContent] = useState({ sections: [], sponsors: [], team_members: [] });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setContent(await adminGetContent(authHeader));
    } catch (error) {
      toast.error(formatError(error, "Could not load managed content."));
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const saveSection = (existing) => async (payload) => {
    if (existing) await adminUpdatePageSection(existing.id, payload, authHeader);
    else await adminCreatePageSection(payload, authHeader);
    toast.success(existing ? "Section updated." : "Section created.");
    await refresh();
  };

  const saveSponsor = (existing) => async (payload) => {
    if (existing) await adminUpdateSponsor(existing.id, payload, authHeader);
    else await adminCreateSponsor(payload, authHeader);
    toast.success(existing ? "Sponsor updated." : "Sponsor created.");
    await refresh();
  };

  const saveTeamMember = (existing) => async (payload) => {
    if (existing) await adminUpdateTeamMember(existing.id, payload, authHeader);
    else await adminCreateTeamMember(payload, authHeader);
    toast.success(existing ? "Team member updated." : "Team member created.");
    await refresh();
  };

  const remove = async (kind, id) => {
    try {
      if (kind === "section") await adminDeletePageSection(id, authHeader);
      if (kind === "sponsor") await adminDeleteSponsor(id, authHeader);
      if (kind === "team") await adminDeleteTeamMember(id, authHeader);
      toast.success("Deleted.");
      await refresh();
    } catch (error) {
      toast.error(formatError(error, "Delete failed."));
    }
  };

  if (loading && content.sections.length === 0) {
    return <p className="font-oswald uppercase tracking-widest text-[#A3A3A3]">Loading content…</p>;
  }

  return (
    <Tabs defaultValue="sections" className="w-full">
      <TabsList className="bg-[#0a0a0a] border border-white/10 rounded-none p-0 h-auto flex-wrap">
        <TabsTrigger value="sections" className="rounded-none font-oswald uppercase tracking-[0.15em] text-xs data-[state=active]:bg-[#E85A24] data-[state=active]:text-black text-[#A3A3A3] px-6 py-3">Sections ({content.sections.length})</TabsTrigger>
        <TabsTrigger value="sponsors" className="rounded-none font-oswald uppercase tracking-[0.15em] text-xs data-[state=active]:bg-[#E85A24] data-[state=active]:text-black text-[#A3A3A3] px-6 py-3">Sponsors ({content.sponsors.length})</TabsTrigger>
        <TabsTrigger value="team" className="rounded-none font-oswald uppercase tracking-[0.15em] text-xs data-[state=active]:bg-[#E85A24] data-[state=active]:text-black text-[#A3A3A3] px-6 py-3">Team ({content.team_members.length})</TabsTrigger>
      </TabsList>

      <TabsContent value="sections" className="mt-8">
        <div className="flex justify-end mb-4">
          <RecordDialog kind="section" title="New Page Section" initial={{ ...emptySection, sort_order: content.sections.length * 10 }} onSave={saveSection(null)} authHeader={authHeader} trigger={<button className={goldBtn}><Plus size={14} /> Add Section</button>} />
        </div>
        <div className="border border-white/10 divide-y divide-white/10">
          {content.sections.map((section) => (
            <div key={section.id} className="flex items-center justify-between gap-4 p-4 hover:bg-white/[0.02]">
              <div className="flex items-center gap-4 min-w-0">
                {section.image && <img src={section.image} alt="" className="w-14 h-12 object-cover grayscale shrink-0" />}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-oswald uppercase text-[#FAFAFA]">{section.key}</p>
                    <Status active={section.is_active} />
                  </div>
                  <p className="font-dm text-xs text-[#A3A3A3] truncate">{[section.eyebrow, section.title, section.title_accent].filter(Boolean).join(" · ") || "Structured content section"}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <RecordDialog kind="section" title={`Edit ${section.key}`} initial={section} onSave={saveSection(section)} authHeader={authHeader} trigger={<button className="text-[#A3A3A3] hover:text-[#E85A24] p-2"><Pencil size={16} /></button>} />
                <DeleteButton label="section" onConfirm={() => remove("section", section.id)} />
              </div>
            </div>
          ))}
          {content.sections.length === 0 && <EmptyState>No page sections yet.</EmptyState>}
        </div>
      </TabsContent>

      <TabsContent value="sponsors" className="mt-8">
        <div className="flex justify-end mb-4">
          <RecordDialog kind="sponsor" title="New Sponsor" initial={{ ...emptySponsor, sort_order: content.sponsors.length }} onSave={saveSponsor(null)} authHeader={authHeader} trigger={<button className={goldBtn}><Plus size={14} /> Add Sponsor</button>} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {content.sponsors.map((sponsor) => (
            <div key={sponsor.id} className="flex items-center justify-between gap-4 p-4 bg-[#050505]">
              <div className="flex items-center gap-4 min-w-0">
                {sponsor.logo ? <img src={sponsor.logo} alt="" className="w-16 h-12 object-contain bg-white/5 shrink-0" /> : <div className="w-16 h-12 border border-white/10 shrink-0" />}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-oswald uppercase text-[#FAFAFA] truncate">{sponsor.name}</p>
                    <Status active={sponsor.is_active} />
                  </div>
                  <p className="font-dm text-xs text-[#A3A3A3]">{sponsor.tier || "Sponsor"} · Order {sponsor.sort_order}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <RecordDialog kind="sponsor" title="Edit Sponsor" initial={sponsor} onSave={saveSponsor(sponsor)} authHeader={authHeader} trigger={<button className="text-[#A3A3A3] hover:text-[#E85A24] p-2"><Pencil size={16} /></button>} />
                <DeleteButton label="sponsor" onConfirm={() => remove("sponsor", sponsor.id)} />
              </div>
            </div>
          ))}
          {content.sponsors.length === 0 && <EmptyState>No sponsors yet.</EmptyState>}
        </div>
      </TabsContent>

      <TabsContent value="team" className="mt-8">
        <div className="flex justify-end mb-4">
          <RecordDialog kind="team" title="New Team Member" initial={{ ...emptyTeamMember, sort_order: content.team_members.length }} onSave={saveTeamMember(null)} authHeader={authHeader} trigger={<button className={goldBtn}><Plus size={14} /> Add Team Member</button>} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {content.team_members.map((member) => (
            <div key={member.id} className="flex items-center justify-between gap-4 p-4 bg-[#050505]">
              <div className="flex items-center gap-4 min-w-0">
                {member.image ? <img src={member.image} alt="" className="w-12 h-12 object-cover grayscale shrink-0" /> : <div className="w-12 h-12 border border-white/10 shrink-0" />}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-3">
                    <p className="font-oswald uppercase text-[#FAFAFA] truncate">{member.name}</p>
                    <Status active={member.is_active} />
                  </div>
                  <p className="font-dm text-xs text-[#A3A3A3]">{member.role} · Order {member.sort_order}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <RecordDialog kind="team" title="Edit Team Member" initial={member} onSave={saveTeamMember(member)} authHeader={authHeader} trigger={<button className="text-[#A3A3A3] hover:text-[#E85A24] p-2"><Pencil size={16} /></button>} />
                <DeleteButton label="team member" onConfirm={() => remove("team", member.id)} />
              </div>
            </div>
          ))}
          {content.team_members.length === 0 && <EmptyState>No team members yet.</EmptyState>}
        </div>
      </TabsContent>
    </Tabs>
  );
}
